import type { JSONContent } from '@tiptap/react'

import type { ButtonAttrs, EmailDocument, SpacerAttrs } from '../types'

/* ----------------------------------------------------------------------------
   Tiptap JSON -> email-safe HTML
   
   Design principles:
   1. All styling is INLINE (email clients strip <style> tags).
   2. Prefer structural HTML (<table><tr><td>) only for "tricky" blocks like
      buttons where Outlook breaks on flexbox/padding. For plain flow content
      we stay on <p>, <ul>, <h*> which render consistently everywhere.
   3. Variables serialise to `{{ key }}` or `{{ key,fallback=value }}` so
      whatever template engine ships the email (liquid / custom / etc.) can
      interpolate them. Matches Maily's format and what tagada already uses
      (see `src/lib/email/templateRenderer.ts`).
---------------------------------------------------------------------------- */

export interface RenderOptions {
    /** Replace variables inline with actual values (used for preview) */
    variableValues?: Record<string, string>
    /** If true, emit pretty-printed HTML with newlines */
    pretty?: boolean
}

export function renderEmailDocument(
    doc: EmailDocument | null,
    options: RenderOptions = {},
): string {
    if (!doc || !doc.content) return ''
    const nl = options.pretty ? '\n' : ''
    return doc.content
        .map((node) => renderNode(node, options))
        .filter(Boolean)
        .join(nl)
}

function renderNode(node: JSONContent, opts: RenderOptions): string {
    switch (node.type) {
        case 'paragraph':
            return renderParagraph(node, opts)
        case 'heading':
            return renderHeading(node, opts)
        case 'bulletList':
            return renderList(node, 'ul', opts)
        case 'orderedList':
            return renderList(node, 'ol', opts)
        case 'listItem':
            return `<li style="margin:0 0 4px 0">${renderInline(node.content || [], opts)}</li>`
        case 'blockquote':
            return renderBlockquote(node, opts)
        case 'horizontalRule':
            return `<hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0" />`
        case 'hardBreak':
            return '<br />'
        case 'image':
            return renderImage(node)
        case 'emailButton':
            return renderButton(node.attrs as ButtonAttrs, opts)
        case 'emailSpacer':
            return renderSpacer(node.attrs as SpacerAttrs)
        case 'emailVariable':
            return renderVariableText(node, opts)
        default:
            // Unknown block — attempt to render its children so we don't lose content
            if (node.content) return renderInline(node.content, opts)
            return ''
    }
}

/* ----------------------------------------------------------------------------
   Block renderers
---------------------------------------------------------------------------- */

function renderParagraph(node: JSONContent, opts: RenderOptions): string {
    const align = (node.attrs?.textAlign as string) || 'left'
    const inner = renderInline(node.content || [], opts)
    if (!inner) return `<p style="margin:0 0 12px 0;line-height:1.6">&nbsp;</p>`
    return `<p style="margin:0 0 12px 0;line-height:1.6;text-align:${align};color:#374151;font-size:15px">${inner}</p>`
}

function renderHeading(node: JSONContent, opts: RenderOptions): string {
    const level = Math.min(Math.max(Number(node.attrs?.level) || 2, 1), 3)
    const align = (node.attrs?.textAlign as string) || 'left'
    const sizes = { 1: 30, 2: 24, 3: 18 } as const
    const weights = { 1: 700, 2: 700, 3: 600 } as const
    const spacing = { 1: '28px 0 12px 0', 2: '24px 0 10px 0', 3: '20px 0 8px 0' } as const
    const inner = renderInline(node.content || [], opts)
    return `<h${level} style="margin:${spacing[level as 1 | 2 | 3]};font-size:${sizes[level as 1 | 2 | 3]}px;font-weight:${weights[level as 1 | 2 | 3]};line-height:1.3;color:#111827;text-align:${align}">${inner}</h${level}>`
}

function renderList(node: JSONContent, tag: 'ul' | 'ol', opts: RenderOptions): string {
    const inner = (node.content || []).map((c) => renderNode(c, opts)).join('')
    return `<${tag} style="margin:0 0 12px 0;padding-left:22px;color:#374151;font-size:15px;line-height:1.6">${inner}</${tag}>`
}

function renderBlockquote(node: JSONContent, opts: RenderOptions): string {
    const inner = (node.content || []).map((c) => renderNode(c, opts)).join('')
    return `<blockquote style="margin:16px 0;padding:8px 16px;border-left:3px solid #e5e7eb;color:#4b5563;font-style:italic">${inner}</blockquote>`
}

function renderImage(node: JSONContent): string {
    const src = (node.attrs?.src as string) || ''
    const alt = (node.attrs?.alt as string) || ''
    const width = node.attrs?.width ? String(node.attrs.width) : '100%'
    const safeAlt = escapeAttr(alt)
    return `<p style="margin:16px 0;text-align:center"><img src="${escapeAttr(src)}" alt="${safeAlt}" width="${escapeAttr(width)}" style="max-width:100%;height:auto;border:0;display:inline-block" /></p>`
}

const RADIUS_PX = { sharp: 0, smooth: 6, round: 9999 } as const

function renderButton(attrs: ButtonAttrs, opts: RenderOptions): string {
    const isFilled = attrs.variant === 'filled'
    const bg = attrs.bgColor || '#111111'
    const fg = attrs.textColor || (isFilled ? '#ffffff' : '#111111')
    const border = attrs.bgColor || '#111111'
    const radius = RADIUS_PX[attrs.radius]
    const align = attrs.align
    const url = applyVariables(attrs.url, opts)
    const label = escapeHtml(applyVariables(attrs.text, opts))

    const buttonStyle = [
        `display:inline-block`,
        `padding:12px 24px`,
        `font-family:inherit`,
        `font-size:15px`,
        `font-weight:600`,
        `line-height:1`,
        `text-decoration:none`,
        `text-align:center`,
        `border-radius:${radius}px`,
        isFilled
            ? `background-color:${bg};color:${fg};border:1px solid ${bg}`
            : `background-color:transparent;color:${fg};border:2px solid ${border}`,
    ].join(';')

    // <table> wrapper for Outlook compatibility
    return [
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:16px 0;width:100%">`,
        `  <tr>`,
        `    <td align="${align}" style="text-align:${align}">`,
        `      <a href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer" style="${buttonStyle}">${label}</a>`,
        `    </td>`,
        `  </tr>`,
        `</table>`,
    ].join('')
}

function renderSpacer(attrs: SpacerAttrs): string {
    const h = Math.max(0, Math.min(500, Number(attrs.height) || 24))
    // Use both height attr and style for widest client support
    return `<div style="height:${h}px;line-height:${h}px;font-size:1px">&nbsp;</div>`
}

function renderVariableText(node: JSONContent, opts: RenderOptions): string {
    const key = (node.attrs?.key as string) || ''
    const fallback = (node.attrs?.fallback as string) || ''
    const tag = fallback
        ? `{{ ${key},fallback=${fallback} }}`
        : `{{ ${key} }}`
    if (opts.variableValues && opts.variableValues[key] !== undefined) {
        return escapeHtml(opts.variableValues[key])
    }
    return tag
}

/* ----------------------------------------------------------------------------
   Inline renderer (text + marks)
---------------------------------------------------------------------------- */

function renderInline(content: JSONContent[], opts: RenderOptions): string {
    return content.map((c) => renderInlineNode(c, opts)).join('')
}

function renderInlineNode(node: JSONContent, opts: RenderOptions): string {
    if (node.type === 'text') {
        return applyMarks(escapeHtml(applyVariables(node.text || '', opts)), node.marks || [])
    }
    if (node.type === 'hardBreak') return '<br />'
    if (node.type === 'emailVariable') return renderVariableText(node, opts)
    if (node.type === 'image') return renderImage(node)
    // Fallback: render children if any
    if (node.content) return renderInline(node.content, opts)
    return ''
}

interface Mark {
    type: string
    attrs?: Record<string, unknown>
}

function applyMarks(text: string, marks: Mark[]): string {
    // Order matters for nesting: outer → inner. We go link → text-style → bold → italic → underline → strike
    const ORDER = ['link', 'textStyle', 'bold', 'italic', 'underline', 'strike']
    const sorted = [...marks].sort(
        (a, b) => ORDER.indexOf(a.type) - ORDER.indexOf(b.type),
    )
    let out = text
    for (const mark of sorted) {
        out = wrapMark(out, mark)
    }
    return out
}

function wrapMark(text: string, mark: Mark): string {
    switch (mark.type) {
        case 'bold':
            return `<strong>${text}</strong>`
        case 'italic':
            return `<em>${text}</em>`
        case 'underline':
            return `<u>${text}</u>`
        case 'strike':
            return `<s>${text}</s>`
        case 'link': {
            const href = escapeAttr((mark.attrs?.href as string) || '#')
            const target = (mark.attrs?.target as string) || '_blank'
            return `<a href="${href}" target="${escapeAttr(target)}" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline">${text}</a>`
        }
        case 'textStyle': {
            const color = mark.attrs?.color as string | undefined
            if (!color) return text
            return `<span style="color:${escapeAttr(color)}">${text}</span>`
        }
        default:
            return text
    }
}

/* ----------------------------------------------------------------------------
   Utilities
---------------------------------------------------------------------------- */

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

function escapeAttr(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

function applyVariables(input: string, opts: RenderOptions): string {
    if (!opts.variableValues) return input
    return input.replace(/\{\{\s*([a-zA-Z0-9_.]+)(?:,\s*fallback=([^}]+))?\s*\}\}/g, (_, key: string, fb?: string) => {
        const val = opts.variableValues?.[key]
        if (val !== undefined) return val
        return fb !== undefined ? fb : `{{ ${key} }}`
    })
}
